import { Avatar, TableCell, TableRow, Typography } from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import moment from 'moment/moment';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { People, fetchFromOldApi } from '~/services/Http';
import { EvaluationSummaryDto } from '~/services/Http/client.people.generated';
import { useGetAppraisalEvaluations } from '~/services/Http/hooks';

import { useSxStyles } from './useStyles';

export interface MyPerformanceProps {
  tableRow: People.PerformanceGoalFullDto;
  isTabDisabled?: boolean;
}

const handleRowClick = (empId: number) => {
  window.location.href = `/page-ui2-redirect?empId=${empId}&area=performance`;
};

export const MyPerformance: React.FC<MyPerformanceProps> = ({
  tableRow,
  isTabDisabled,
}) => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const widgetContentRef = React.useRef<HTMLTableRowElement>(null);
  const { width: widgetContentWidth } = useComponentSize(widgetContentRef);
  const minSizeRender = widgetContentWidth < 320;

  const [latestV2Score, setLatestV2Score] = React.useState<number>(0);
  const [appraisalName, setAppraisalName] = React.useState<string>('');
  const statusNotStarted = t('notStarted');
  const [appraisalStatus, setAppraisalStatus] =
    React.useState<string>(statusNotStarted);
  const [appraisalDate, setAppraisalDate] = React.useState<string>('');

  const statusTranslation = useCallback(
    (status: string) => {
      switch (status) {
        case 'IN_PROGRESS':
          return t('inProgress');
        case 'COMPLETED':
          return t('completed');
        case 'EMPLOYEE_APPROVED':
          return t('laEmployeeApproved');
        default:
          return status;
      }
    },
    [t],
  );

  const { data: appraisalEvaluations, loading: loadingAppraisals } =
    useGetAppraisalEvaluations({});

  useEffect(() => {
    const fetchLatestV2Score = async () => {
      try {
        const response = await fetchFromOldApi(
          `/json/performance/goals/employee/${tableRow.employee.id}/evaluations`,
        );
        const data = await response.json();
        if (data.length > 0) {
          const lastEvaluation = data[data.length - 1];
          setLatestV2Score(lastEvaluation.totScore);
        }
      } catch (error) {
        console.error('Error fetching evaluations:', error);
      }
    };

    if (!loadingAppraisals) {
      const fetchAppraisals = async () => {
        try {
          const response = await fetchFromOldApi(
            `/json/appraisal/templates/full/${tableRow.employee.id}`,
          );
          const result = await response.json();
          const data = result.data;
          if (data.length > 0) {
            let activeTemplates = data.filter(
              (appraisal: { status: string; deadline: string }) =>
                appraisal.status === 'ACTIVE' &&
                appraisal.deadline > new Date().toISOString(),
            );

            if (activeTemplates.length === 0) {
              activeTemplates = data.filter(
                (appraisal: { status: string }) =>
                  appraisal.status === 'ACTIVE',
              );
            }

            if (activeTemplates.length > 0) {
              const activeTemplateIds = activeTemplates.map(
                (template: { id: string }) => template.id,
              );
              const matchingEvaluations = appraisalEvaluations?.filter(
                (evaluation: EvaluationSummaryDto) =>
                  evaluation.employee.id === tableRow.employee.id &&
                  activeTemplateIds.includes(evaluation.templateId),
              );

              if (matchingEvaluations && matchingEvaluations.length > 0) {
                const latestEvaluation = matchingEvaluations.reduce(
                  (latest, current) =>
                    (current.lastUpdatedAt ?? 0) > (latest.lastUpdatedAt ?? 0)
                      ? current
                      : latest,
                );

                const matchingTemplate = activeTemplates.find(
                  (template: { id: string }) =>
                    template.id === latestEvaluation.templateId,
                );

                setAppraisalName(matchingTemplate?.subject ?? '');
                setAppraisalStatus(statusTranslation(latestEvaluation.status));
                setAppraisalDate(
                  latestEvaluation.lastUpdatedAt?.toString() || '',
                );
              } else {
                const latestTemplate = activeTemplates.reduce(
                  (
                    latest: { updatedTime: string },
                    current: { updatedTime: string },
                  ) =>
                    (current.updatedTime ?? 0) > (latest.updatedTime ?? 0)
                      ? current
                      : latest,
                );

                setAppraisalName(latestTemplate.subject);
                setAppraisalStatus(statusNotStarted);
                setAppraisalDate('');
              }
            } else {
              setAppraisalName('');
              setAppraisalStatus('');
              setAppraisalDate('');
            }
          }
        } catch (error) {
          console.error('Error fetching evaluations:', error);
        }
      };

      fetchLatestV2Score();
      fetchAppraisals();
    }
  }, [
    appraisalEvaluations,
    loadingAppraisals,
    tableRow.employee.id,
    statusNotStarted,
    statusTranslation,
  ]);

  const tableRowProps = isTabDisabled
    ? {
        hover: !isTabDisabled,
      }
    : {
        sx: sxStyles.row,
        onClick: () => handleRowClick(tableRow.employee.id),
        hover: !isTabDisabled,
      };

  return (
    <TableRow {...tableRowProps} ref={widgetContentRef}>
      <TableCell sx={sxStyles.cell} data-cy="profile-pic">
        <Avatar
          alt={tableRow.employee.firstName}
          src={tableRow.employee.profilePic}
        />
      </TableCell>
      {minSizeRender ? (
        <TableCell align="right" sx={sxStyles.cell}>
          <Typography
            component="div"
            sx={sxStyles.nameCell}
            variant="body2"
            data-cy="name"
          >
            {`${tableRow.employee!.firstName?.charAt(
              0,
            )}. ${tableRow.employee!.lastName?.charAt(0)}.`}
          </Typography>
        </TableCell>
      ) : (
        <TableCell sx={sxStyles.cell}>
          <Typography component="div" variant="body2">
            <TooltippedTruncatedText dataCy="name">
              {`${tableRow.employee!.firstName} ${tableRow.employee!.lastName}`}
            </TooltippedTruncatedText>
          </Typography>
        </TableCell>
      )}
      <TableCell align="right" sx={sxStyles.cell}>
        <TooltippedTruncatedText data-cy="score">
          {tableRow.scores.total}
        </TooltippedTruncatedText>
      </TableCell>
      <TableCell align="right" sx={sxStyles.cell}>
        <TooltippedTruncatedText data-cy="score2">
          {latestV2Score}
        </TooltippedTruncatedText>
      </TableCell>
      <TableCell align="right" sx={sxStyles.cell}>
        <TooltippedTruncatedText data-cy="appraisal">
          {appraisalName}
        </TooltippedTruncatedText>
      </TableCell>
      <TableCell align="right" sx={sxStyles.cell}>
        <TooltippedTruncatedText data-cy="appraisal-status">
          {appraisalStatus}
        </TooltippedTruncatedText>
      </TableCell>
      <TableCell align="right" sx={sxStyles.cell}>
        <TooltippedTruncatedText data-cy="appraisal-modified">
          {!!appraisalDate && appraisalStatus !== statusNotStarted
            ? moment(appraisalDate).format('L LT')
            : ''}
        </TooltippedTruncatedText>
      </TableCell>
    </TableRow>
  );
};
